import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from "react-router-dom";
import SubNav from "../components/subNav";
import SEO from '../components/SEO';
import { Camera, Loader } from 'lucide-react';
import '../styles/settings.css';
import { baseUrl } from '../config';

const LANGUAGES = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Español' },
    { code: 'fr', name: 'Français' },
    { code: 'de', name: 'Deutsch' },
    { code: 'zh', name: '中文' },
    { code: 'ja', name: '日本語' },
    { code: 'ru', name: 'Русский' },
    { code: 'ar', name: 'العربية' },
    { code: 'pt', name: 'Português' },
    { code: 'hi', name: 'हिन्दी' },
    { code: 'it', name: 'Italiano' },
    { code: 'ko', name: '한국어' },
    { code: 'tr', name: 'Türkçe' },
    { code: 'nl', name: 'Nederlands' },
    { code: 'sv', name: 'Svenska' },
    { code: 'pl', name: 'Polski' },
    { code: 'da', name: 'Dansk' },
    { code: 'fi', name: 'Suomi' },
    { code: 'no', name: 'Norsk' },
    { code: 'he', name: 'עברית' },
    { code: 'th', name: 'ไทย' },
    { code: 'vi', name: 'Tiếng Việt' },
    { code: 'id', name: 'Bahasa Indonesia' },
    { code: 'ms', name: 'Bahasa Melayu' },
    { code: 'bn', name: 'বাংলা' },
    { code: 'gu', name: 'ગુજરાતી' },
    { code: 'kn', name: 'ಕನ್ನಡ' },
    { code: 'ml', name: 'മലയാളം' },
    { code: 'mr', name: 'मराठी' },
    { code: 'pa', name: 'ਪੰਜਾਬੀ' },
    { code: 'ta', name: 'தமிழ்' },
    { code: 'te', name: 'తెలుగు' },
    { code: 'ur', name: 'اردو' }
];

export function Settings() {
    const navigate = useNavigate();
    const [userId, setUserId] = useState(null);
    const [formData, setFormData] = useState({
        profPic: '',
        username: '',
        email: '',
        phoneNum: '',
        firstName: '',
        lastName: '',
        address1: '',
        address2: '',
        state: '',
        country: '',
        postalCode: '',
        role: '',
        lang: 'en',
        lastLogin: new Date(),
        loginCount: 0,
        timeZone: '',
        gender: '',
        birthday: ''
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitMessage, setSubmitMessage] = useState('');
    const [locale, setLocale] = useState(localStorage.getItem('locale') || 'en');
    const [previewImage, setPreviewImage] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isUploadingImage, setIsUploadingImage] = useState(false);

    /* Fetch user data from backend */
    const fetchUserData = async () => {
        try {
            setIsLoading(true);
            
            const token = localStorage.getItem("access_token");
            if (!token) {
                navigate("/login");
                return;
            }
            
            const response = await fetch(`${baseUrl}/getUserById`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
            });

            if (response.ok) {
                const userData = await response.json();
                setUserId(userData.user_id);
                
                setFormData({
                    profPic: userData.profile_picture || '',
                    username: userData.username || '',
                    email: userData.email || '',
                    phoneNum: userData.phone_number || '',
                    firstName: userData.first_name || '',
                    lastName: userData.last_name || '',
                    address1: userData.address_line1 || '',
                    address2: userData.address_line2 || '',
                    state: userData.state || '',
                    country: userData.country || '',
                    postalCode: userData.postal_code || '',
                    role: userData.role || '',
                    lang: userData.lang || 'en',
                    lastLogin: userData.created_at ? new Date(userData.created_at) : new Date(),
                    loginCount: 0,
                    timeZone: '',
                    gender: userData.gender || '',
                    birthday: userData.birthday || ''
                });

                // Set preview image if profile picture exists
                if (userData.profile_picture) {
                    setPreviewImage(userData.profile_picture);
                }

                if (userData.lang) {
                    setLocale(userData.lang);
                }
            } else if (response.status === 401) {
                navigate("/login");
            } else {
                const errorText = await response.text();
                setSubmitMessage('Failed to load user data. Please try again.');
            }
        } catch (error) {
            setSubmitMessage('Error loading user data. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchUserData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prevData => ({
            ...prevData,
            [name]: value
        }));
        
        if (errors[name]) {
            setErrors(prevErrors => ({
                ...prevErrors,
                [name]: ''
            }));
        }
    };

    const handleFileChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        if (file && file.type.substr(0, 5) === "image") {
            setIsUploadingImage(true);
            setSubmitMessage('Uploading profile picture...');
            
            try {
                const uploadFormData = new FormData();
                uploadFormData.append('file', file);
                uploadFormData.append('file_type', 'image');
                uploadFormData.append('user_id', userId || 0);
                
                const uploadResponse = await fetch(`${baseUrl}/upload/direct`, {
                    method: 'POST',
                    body: uploadFormData
                });
                
                if (!uploadResponse.ok) {
                    throw new Error('Failed to upload image');
                }
                
                const uploadData = await uploadResponse.json();
                const imageUrl = uploadData.public_url;
                
                const token = localStorage.getItem("access_token");
                const updateResponse = await fetch(`${baseUrl}/updateProfilePicture`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'profile_picture': imageUrl
                    }
                });
                
                if (updateResponse.ok) {
                    setFormData(prevData => ({
                        ...prevData,
                        profPic: imageUrl
                    }));
                    setPreviewImage(imageUrl);
                    setSubmitMessage('Profile picture updated successfully!');
                    
                    // Trigger SubNav refresh by dispatching custom event
                    window.dispatchEvent(new Event('profileUpdated'));
                } else {
                    throw new Error('Failed to update profile picture');
                }
                
            } catch (error) {
                setSubmitMessage('Failed to upload profile picture. Please try again.');
                setErrors(prevErrors => ({
                    ...prevErrors,
                    profPic: 'Failed to upload image'
                }));
            } finally {
                setIsUploadingImage(false);
            }
        } else {
            setErrors(prevErrors => ({
                ...prevErrors,
                profPic: 'Please select a valid image file'
            }));
        }
    };

    const validateForm = () => {
        let formErrors = {};
        
        // ✅ FIXED: Only validate non-empty fields
        if (!formData.firstName || !formData.firstName.trim()) {
            formErrors.firstName = 'First name is required';
        }
        
        if (!formData.email || !formData.email.trim()) {
            formErrors.email = 'Email is required';
        } else if (!formData.email.match(/^[\w\-\.]+@([\w\-]+\.)+[\w\-]{2,}$/)) {
            formErrors.email = 'Please enter a valid email address';
        }
        
        // ✅ FIXED: Only validate phone if provided
        if (formData.phoneNum && formData.phoneNum.trim() && !formData.phoneNum.match(/^\+?[0-9\s\-\(\)]{7,}$/)) {
            formErrors.phoneNum = 'Please enter a valid phone number';
        }
        
        setErrors(formErrors);
        return Object.keys(formErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            setSubmitMessage('Please fix the errors in the form');
            return;
        }

        setIsSubmitting(true);
        setSubmitMessage('Saving changes...');

        try {
            const token = localStorage.getItem("access_token");
            if (!token) {
                setSubmitMessage('User session expired. Please log in again.');
                navigate("/login");
                return;
            }
            
            const updateData = {
                username: `${formData.firstName.trim()} ${formData.lastName.trim()}`.trim(),
                email: formData.email,
                phone_number: formData.phoneNum,
                first_name: formData.firstName,
                last_name: formData.lastName,
                address_line1: formData.address1,
                address_line2: formData.address2,
                state: formData.state,
                country: formData.country,
                postal_code: formData.postalCode,
                role: formData.role,
                gender: formData.gender,
                lang: formData.lang,
                birthday: formData.birthday || null
            };

            const response = await fetch(`${baseUrl}/updateUser`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                },
                body: JSON.stringify(updateData)
            });

            if (response.ok) {
                setSubmitMessage('Settings updated successfully!');
                setLocale(formData.lang);
                localStorage.setItem('locale', formData.lang);
                
                // Trigger SubNav refresh
                window.dispatchEvent(new Event('profileUpdated'));
                
                await fetchUserData();
            } else {
                const errorData = await response.json();
                throw new Error(errorData.detail || 'Failed to update settings');
            }

        } catch (error) {
            setSubmitMessage(error.message || 'Failed to update settings. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleLanguageChange = (e) => {
        const newLang = e.target.value;
        setFormData(prevData => ({
            ...prevData,
            lang: newLang
        }));
        setLocale(newLang);
        localStorage.setItem('locale', newLang);
    };

    if (isLoading) {
        return (
            <div className="settings-page">
                <SubNav />
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 'calc(100vh - 120px)',
                    gap: '16px'
                }}>
                    <Loader className="animate-spin" size={40} style={{ color: '#0ea5e9' }} />
                    <div style={{ fontSize: '16px', color: '#666' }}>Loading user settings...</div>
                </div>
            </div>
        );
    }

    return (
        <div className="settings-page">
            <SEO
              title="Settings"
              description="Manage your ProducerBuddy account settings, notifications, and preferences."
              url="https://producerbuddy.pilomation.com/settings"
              robots="noindex, nofollow"
            />
            <SubNav />
            <div className="welcome-banner">
                <div className="welcome-content">
                    <h1>Welcome, {formData.firstName || formData.username || 'User'}</h1>
                    <p>{new Date().toLocaleDateString()}</p>
                </div>
            </div>

            <div className="settings-container">
                {submitMessage && (
                    <div className={`message ${submitMessage.includes('Failed') || submitMessage.includes('Error') || submitMessage.includes('fix') ? 'error' : 'success'}`}>
                        {submitMessage}
                    </div>
                )}
                
                <div className="profile-header">
                    <div className="profile-picture-container">
                        {isUploadingImage ? (
                            <div className="profile-picture-placeholder">
                                <Loader className="animate-spin" size={40} />
                            </div>
                        ) : (
                            <>
                                {previewImage || formData.profPic ? (
                                    <img 
                                        src={previewImage || formData.profPic} 
                                        alt="Profile" 
                                        className="profile-picture"
                                    />
                                ) : (
                                    <div className="profile-picture-placeholder">
                                        <Camera size={40} />
                                    </div>
                                )}
                            </>
                        )}
                        <label htmlFor="profPic" className="upload-button" style={{ 
                            backgroundColor: '#3b82f6', 
                            color: '#ffffff',
                            padding: '8px 16px',
                            borderRadius: '6px',
                            cursor: isUploadingImage ? 'not-allowed' : 'pointer',
                            opacity: isUploadingImage ? 0.6 : 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            border: 'none',
                            fontWeight: '500'
                        }}>
                            <Camera size={16} />
                            <span>Change Photo</span>
                            <input 
                                type="file" 
                                id="profPic" 
                                onChange={handleFileChange} 
                                accept="image/*" 
                                style={{ display: 'none' }}
                                disabled={isUploadingImage}
                            />
                        </label>
                    </div>
                    <div className="profile-info">
                        <h2>{formData.firstName && formData.lastName ? `${formData.firstName} ${formData.lastName}` : formData.username || 'User'}</h2>
                        <p>{formData.role || 'No role specified'}</p>
                        <p>{formData.email}</p>
                    </div>
                </div>

                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="form-section">
                            <h3>Personal Information</h3>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="firstName">First Name *</label>
                                    <input 
                                        type="text" 
                                        name="firstName" 
                                        id="firstName" 
                                        value={formData.firstName} 
                                        onChange={handleInputChange} 
                                        required 
                                    />
                                    {errors.firstName && <span className="error">{errors.firstName}</span>}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="lastName">Last Name</label>
                                    <input 
                                        type="text" 
                                        name="lastName" 
                                        id="lastName" 
                                        value={formData.lastName} 
                                        onChange={handleInputChange} 
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="gender">Gender</label>
                                    <select 
                                        name="gender" 
                                        id="gender" 
                                        value={formData.gender} 
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Gender</option>
                                        <option value="male">Male</option>
                                        <option value="female">Female</option>
                                        <option value="other">Other</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="birthday">Birthday</label>
                                    <input 
                                        type="date" 
                                        name="birthday" 
                                        id="birthday" 
                                        value={formData.birthday} 
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="timeZone">Time Zone</label>
                                    <select 
                                        name="timeZone" 
                                        id="timeZone" 
                                        value={formData.timeZone} 
                                        onChange={handleInputChange}
                                    >
                                        <option value="">Select Time Zone</option>
                                        <option value="UTC">UTC</option>
                                        <option value="EST">EST</option>
                                        <option value="PST">PST</option>
                                        <option value="IST">IST</option>
                                        <option value="GMT">GMT</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>Contact Information</h3>
                            <div className="form-group">
                                <label htmlFor="email">Email *</label>
                                <input 
                                    type="email" 
                                    name="email" 
                                    id="email" 
                                    value={formData.email} 
                                    onChange={handleInputChange} 
                                    required 
                                />
                                {errors.email && <span className="error">{errors.email}</span>}
                            </div>
                            <div className="form-group">
                                <label htmlFor="phoneNum">Phone Number</label>
                                <input 
                                    type="tel" 
                                    name="phoneNum" 
                                    id="phoneNum" 
                                    value={formData.phoneNum} 
                                    onChange={handleInputChange} 
                                />
                                {errors.phoneNum && <span className="error">{errors.phoneNum}</span>}
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>Address</h3>
                            <div className="form-group">
                                <label htmlFor="address1">Address Line 1</label>
                                <input 
                                    type="text" 
                                    name="address1" 
                                    id="address1" 
                                    value={formData.address1} 
                                    onChange={handleInputChange} 
                                />
                            </div>
                            <div className="form-group">
                                <label htmlFor="address2">Address Line 2</label>
                                <input 
                                    type="text" 
                                    name="address2" 
                                    id="address2" 
                                    value={formData.address2} 
                                    onChange={handleInputChange} 
                                />
                            </div>
                            <div className="form-row">
                                <div className="form-group">
                                    <label htmlFor="state">State</label>
                                    <input 
                                        type="text" 
                                        name="state" 
                                        id="state" 
                                        value={formData.state} 
                                        onChange={handleInputChange} 
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="country">Country</label>
                                    <input 
                                        type="text" 
                                        name="country" 
                                        id="country" 
                                        value={formData.country} 
                                        onChange={handleInputChange} 
                                    />
                                </div>
                            </div>
                            <div className="form-group">
                                <label htmlFor="postalCode">Postal Code</label>
                                <input 
                                    type="text" 
                                    name="postalCode" 
                                    id="postalCode" 
                                    value={formData.postalCode} 
                                    onChange={handleInputChange} 
                                />
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>Professional Information</h3>
                            <div className="form-group">
                                <label htmlFor="role">Role/Position</label>
                                <input 
                                    type="text" 
                                    name="role" 
                                    id="role" 
                                    value={formData.role} 
                                    onChange={handleInputChange} 
                                />
                            </div>
                        </div>

                        <div className="form-section">
                            <h3>Preferences</h3>
                            <div className="form-group">
                                <label htmlFor="lang">Language</label>
                                <select 
                                    name="lang" 
                                    id="lang" 
                                    value={formData.lang} 
                                    onChange={handleLanguageChange}
                                >
                                    {LANGUAGES.map(lang => (
                                        <option key={lang.code} value={lang.code}>
                                            {lang.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="form-footer">
                        <button type="submit" disabled={isSubmitting || isUploadingImage}>
                            {isSubmitting ? 'Saving...' : 'Save Settings'}
                        </button>
                        <NavLink to="/SettingPrivicy" className="reset-password-link">
                            Security & Privacy Settings
                        </NavLink>
                    </div>
                </form>
            </div>
        </div>
    );
}
